import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, MapPin, Check, X as XIcon, Archive, Trash2 } from "lucide-react";

const STATUS_TABS = [
  { v: "all", label: "All (pending first)" },
  { v: "pending_review", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "published", label: "Published" },
  { v: "rejected", label: "Rejected" },
  { v: "archived", label: "Archived" },
  { v: "completed", label: "Completed" },
];

const STATUS_STYLES = {
  pending_review: { bg: "#F5C518", fg: "#1F1B16", label: "PENDING" },
  approved: { bg: "#3C9A5F", fg: "#FDFBF7", label: "APPROVED" },
  published: { bg: "#3C9A5F", fg: "#FDFBF7", label: "PUBLISHED" },
  rejected: { bg: "#B23A3A", fg: "#FDFBF7", label: "REJECTED" },
  archived: { bg: "#7A7A7A", fg: "#FDFBF7", label: "ARCHIVED" },
  completed: { bg: "#5C6B52", fg: "#FDFBF7", label: "COMPLETED" },
};

const ACTION_LABELS = {
  moderate_approved: "Approved",
  moderate_published: "Published",
  moderate_rejected: "Rejected",
  moderate_archived: "Archived",
  moderate_completed: "Marked completed",
  delete: "Deleted",
  edited: "Edited by owner",
};

const URGENCY_COLORS = { emergency: "#B23A3A", high: "#B25D38", normal: "var(--wofy-primary)", low: "#7A7A7A" };
const BADGE_COLORS = { official: "var(--wofy-secondary)", verified_org: "var(--wofy-primary)", verified_authority: "#5C6B52", self_reported: "#7A7A7A" };

export default function AdminCauses() {
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/admin/causes", { params: { status } });
      setItems(data.items);
      setCounts(data.counts);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (newStatus) => {
    try {
      await http.patch(`/admin/causes/${selected.id}`, { status: newStatus, comment });
      toast.success(`Marked ${newStatus}`);
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${selected.title}" permanently?`)) return;
    try {
      await http.delete(`/admin/causes/${selected.id}`, { params: { comment } });
      toast.success("Deleted");
      setSelected(null); setComment("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-causes-panel">
      <div className="overline mb-2">CAUSES / PLEAS</div>
      <h2 className="font-display text-3xl">Moderation queue</h2>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button
            key={s.v}
            onClick={() => setStatus(s.v)}
            data-testid={`admin-causes-filter-${s.v}`}
            className={`px-3 py-1.5 text-sm rounded-full ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}
          >
            {s.label} <span className="ml-2 text-xs font-mono opacity-60">{counts[s.v] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Nothing here.</p>
        ) : items.map((c) => {
          const st = STATUS_STYLES[c.status] || { bg: "#666", fg: "#fff", label: (c.status || "").toUpperCase() };
          const isPending = c.status === "pending_review";
          return (
          <div key={c.id} data-testid={`admin-cause-${c.id}`} className="wofy-card cursor-pointer hover:shadow-md" onClick={() => { setSelected(c); setComment(""); }} style={isPending ? { borderLeft: "4px solid #F5C518" } : {}}>
            <div className="flex items-start gap-4">
              {c.photos?.[0] && <img src={c.photos[0]} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span data-testid={`admin-cause-status-${c.id}`} className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase font-semibold" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase text-white" style={{ background: URGENCY_COLORS[c.urgency] || "#666" }}>{c.urgency}</span>
                  <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase text-white" style={{ background: BADGE_COLORS[c.verification_badge] || "#666" }}>{(c.verification_badge || "").replace("_", " ")}</span>
                  <span className="wofy-tag text-[10px]">{c.category}</span>
                </div>
                <div className="font-display text-lg mt-1">{c.title}</div>
                <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{c.description}</p>
                <div className="mt-2 text-[11px] font-mono flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: "var(--wofy-text-soft)" }}>
                  <span>SUBMITTED: {new Date(c.created_at).toLocaleString()}</span>
                  <span>· {c.author_name}</span>
                  <span className="inline-flex items-center gap-1">· <MapPin className="w-3 h-3" /> {c.location?.city}, {c.location?.country}</span>
                </div>

                {/* Action-taken box: shows Pending state OR last moderation comment */}
                <div className="mt-3 rounded-md border px-3 py-2 text-xs" style={{ borderColor: isPending ? "#F5C518" : "var(--wofy-border)", background: isPending ? "rgba(245,197,24,0.08)" : "var(--wofy-surface)" }}>
                  {isPending ? (
                    <>
                      <span className="font-mono uppercase tracking-wider font-semibold" style={{ color: "#8A6D00" }}>Pending review</span>
                      <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>— awaiting moderator action</span>
                    </>
                  ) : c.last_action ? (
                    <div>
                      <div className="font-mono uppercase tracking-wider">
                        {ACTION_LABELS[c.last_action] || c.last_action}
                        {c.last_action_at && <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>· {new Date(c.last_action_at).toLocaleString()}</span>}
                        {c.last_action_admin && <span className="ml-2" style={{ color: "var(--wofy-text-soft)" }}>· by {c.last_action_admin}</span>}
                      </div>
                      {c.last_action_comment ? (
                        <div className="mt-1 italic" style={{ color: "var(--wofy-text)" }}>"{c.last_action_comment}"</div>
                      ) : (
                        <div className="mt-1" style={{ color: "var(--wofy-text-soft)" }}>No comment provided.</div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: "var(--wofy-text-soft)" }}>No moderation activity yet.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-[720px] overflow-y-auto p-8" style={{ background: "var(--wofy-paper)" }} onClick={(e) => e.stopPropagation()} data-testid="admin-cause-drawer">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">CAUSE</div>
                <h3 className="font-display text-2xl mt-1">{selected.title}</h3>
                <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
                  {selected.author_name} · {selected.category} · {selected.location?.city}, {selected.location?.country}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="wofy-btn-ghost text-xs">Close</button>
            </div>

            {selected.photos?.length > 0 && (
              <div className="mt-4 grid grid-cols-4 gap-2">
                {selected.photos.map((p, i) => <img key={i} src={p} alt="" className="aspect-square w-full rounded object-cover" />)}
              </div>
            )}

            <div className="mt-6 wofy-card whitespace-pre-line text-sm">{selected.description}</div>

            {selected.contact_email && <div className="mt-4 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>Contact: {selected.contact_email} {selected.contact_phone && `· ${selected.contact_phone}`}</div>}

            <div className="mt-6 wofy-card">
              <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Moderation note (optional)</div>
              <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="admin-cause-comment" className="w-full mt-2 rounded-lg border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => act("approved")} data-testid="admin-cause-approve" className="wofy-btn-primary text-sm"><Check className="w-4 h-4" /> Approve &amp; publish</button>
                <button onClick={() => act("rejected")} data-testid="admin-cause-reject" className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><XIcon className="w-4 h-4" /> Reject</button>
                <button onClick={() => act("archived")} className="wofy-btn-ghost text-sm"><Archive className="w-4 h-4" /> Archive</button>
                <button onClick={() => act("completed")} className="wofy-btn-ghost text-sm">Mark completed</button>
                <button onClick={del} className="wofy-btn-ghost text-sm" style={{ color: "#B23A3A" }}><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
